import React, { useEffect, useState } from 'react';
import Datatable from '../../components/Datatable';
import FormDialog from '../../components/Datatable/FormDialog';
import { Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Container, FormControl, FormControlLabel, FormGroup, IconButton, InputLabel, MenuItem, Select, Snackbar, TextField, Tooltip, Typography, setRef } from '@mui/material';
import api, { create, get_one, update, delete_one } from '../../services/api';
import EditIcon from '@mui/icons-material/Edit';
import KeyIcon from '@mui/icons-material/Key';
import { useNotify } from '../../components/Datatable/NotifyProvider';
import { useFormError } from '../../components/Datatable/FormErrorProvider';
import { Close, Done, PlayArrow, RemoveCircleOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { green, red, blue, orange, grey } from '@mui/material/colors';
import { useAuth } from '../../components/AuthProvider';
import moment from 'moment';
import CustomTextField from '../../components/CustomTextField';
import HermesTypeSelect from '../../components/HermesTypeSelect';
import TunnelSelect from '../../components/TunnelSelect';
import BtnZabbix from './Zabbix';
import BackupViewButton from '../../components/BackupView';
import HostFilterBar, { initialHostFilters, toDatatableFilters } from '../../components/HostFilterBar';
import BulkHostDialog from '../../components/BulkHostDialog';
import useSelecaoPorId from '../../hooks/useSelecaoPorId';
import SelecaoHostsBar from '../../components/SelecaoHostsBar';
import SelecaoHostsDialog from '../../components/SelecaoHostsDialog';
import ExecucoesHostDialog from '../../components/ExecucoesHostDialog';
import HistoryIcon from '@mui/icons-material/History';
import useSSE from '../../hooks/useSSE';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TuneIcon from '@mui/icons-material/Tune';

// Célula combinada do Host (nome / IP / vendor — tipo). Estilo do admin.
function HostCell({ name, ip, vendor, type }) {
    return (
        <Box>
            <Typography variant="body2" fontWeight={600}>{name}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">{ip}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                {vendor} — {type}
            </Typography>
        </Box>
    );
}

// Indicador combinado de status do backup do host. Prioridade:
// 1. Redis (queued/running) — estado vivo agora
// 2. Erro pendente (last_log_status=0)
// 3. Último backup OK (mostra data)
// 4. Nunca rodou
function BackupStatus({ vivo, queueStatus, queuePosition, queueProgress, lastLogStatus, lastLogMessage, lastBackupDate, backupCount, unresolvedCount }) {
    // O SSE é mais novo que a linha carregada: enquanto ele fala, ele manda.
    if (vivo) {
        if (vivo.status) queueStatus = vivo.status
        if (vivo.progress) queueProgress = vivo.progress
        if (vivo.position != null) queuePosition = vivo.position
        if (vivo.concluidoComo === 'error') { lastLogStatus = 0; lastLogMessage = vivo.mensagemErro || lastLogMessage }
        if (vivo.concluidoComo === 'success') { lastLogStatus = 1; lastBackupDate = lastBackupDate || new Date().toISOString() }
    }
    if (queueStatus === 'running') {
        return (
            <Box display="flex" alignItems="center" gap={0.75}>
                <SyncIcon sx={{ color: blue[600], fontSize: 18,
                    animation: 'spin 2s linear infinite',
                    '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
                <Box>
                    <Typography variant="body2" sx={{ color: blue[700], fontWeight: 600 }}>
                        Executando
                    </Typography>
                    {queueProgress?.step && (
                        <Typography variant="caption" color="text.secondary">{queueProgress.step}</Typography>
                    )}
                </Box>
            </Box>
        );
    }
    if (queueStatus === 'queued') {
        return (
            <Box display="flex" alignItems="center" gap={0.75}>
                <ScheduleIcon sx={{ color: orange[700], fontSize: 18 }} />
                <Box>
                    <Typography variant="body2" sx={{ color: orange[800], fontWeight: 600 }}>
                        Na fila
                    </Typography>
                    {queuePosition != null && (
                        <Typography variant="caption" color="text.secondary">
                            posição {queuePosition}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }
    if (lastLogStatus === 0) {
        return (
            <Box display="flex" alignItems="center" gap={0.75}>
                <ErrorIcon sx={{ color: red[600], fontSize: 18 }} />
                <Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body2" sx={{ color: red[700], fontWeight: 600 }}>Erro</Typography>
                        {unresolvedCount > 1 && (
                            <Chip label={`${unresolvedCount} pendentes`} size="small"
                                  sx={{ height: 16, bgcolor: red[50], color: red[800], fontWeight: 600 }} />
                        )}
                    </Box>
                    {lastLogMessage && (
                        <Tooltip title={lastLogMessage}>
                            <Typography variant="caption" color="text.secondary" noWrap
                                        sx={{ display: 'block', maxWidth: 260 }}>
                                {lastLogMessage}
                            </Typography>
                        </Tooltip>
                    )}
                </Box>
            </Box>
        );
    }
    if (lastBackupDate) {
        return (
            <Box display="flex" alignItems="center" gap={0.75}>
                <CheckCircleIcon sx={{ color: green[600], fontSize: 18 }} />
                <Box>
                    <Typography variant="body2" sx={{ color: green[700], fontWeight: 600 }}>OK</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {moment(lastBackupDate).locale('pt-br').format('DD/MM HH:mm')}
                        {backupCount > 0 && ` · ${backupCount} backups`}
                    </Typography>
                </Box>
            </Box>
        );
    }
    return (
        <Box display="flex" alignItems="center" gap={0.75}>
            <HelpOutlineIcon sx={{ color: grey[500], fontSize: 18 }} />
            <Typography variant="body2" color="text.secondary">Sem backup ainda</Typography>
        </Box>
    );
}

export default function Host() {
    const notify = useNotify()
    const auth = useAuth()
    const [formState, setFormState] = useState({ open: false, id: null })
    const error = useFormError()
    const [refresh, setRefresh] = useState(false)
    const [runningBackup, setRunningBackup] = useState({})  // hostId -> bool
    const [hostFilters, setHostFilters] = useState(() => initialHostFilters(false))
    const [types, setTypes] = useState([])
    const [tableData, setTableData] = useState([])
    const [bulkDialog, setBulkDialog] = useState(false)
    const [bulkBusy, setBulkBusy] = useState(false)

    // Seleção por id: o índice de linha do MUIDataTable deixa de valer assim
    // que os dados mudam. Ver hooks/useSelecaoPorId.
    const selecao = useSelecaoPorId(tableData)
    const [verSelecao, setVerSelecao] = useState(false)
    const [execDialog, setExecDialog] = useState({ open: false, hostId: null, hostName: '' })

    // Progresso ao vivo. `backupStatus` é o que veio do SSE e tem prioridade
    // sobre a coluna do banco, que é sempre um retrato do último fetch.
    const [backupStatus, setBackupStatus] = useState({})
    const [activeHostIds, setActiveHostIds] = useState([])
    const sseHostIds = activeHostIds.join(',')
    const { data: sseEvent } = useSSE(
        '/hermes/backup/stream',
        { host_ids: sseHostIds },
        sseHostIds.length > 0
    )

    useEffect(() => {
        if (!sseEvent || !sseEvent.host_id) return
        const hostId = sseEvent.host_id
        const progress = sseEvent.progress || sseEvent
        const step = progress?.step || sseEvent?.step

        setBackupStatus((prev) => ({
            ...prev,
            [hostId]: { ...prev[hostId], ...sseEvent, progress }
        }))

        if (step === 'done' || step === 'error') {
            // Sai da lista de monitorados, senão o stream fica aberto pra
            // sempre por um host que já terminou.
            setActiveHostIds((prev) => prev.filter((id) => id !== hostId))
            setBackupStatus((prev) => ({
                ...prev,
                [hostId]: {
                    ...prev[hostId], status: 'idle',
                    concluidoEm: Date.now(),
                    concluidoComo: step === 'done' ? 'success' : 'error',
                    mensagemErro: step === 'error' ? (progress?.message || sseEvent?.message) : null
                }
            }))
            notify.setNotify({
                open: true,
                severity: step === 'done' ? 'success' : 'error',
                message: step === 'done'
                    ? 'Backup concluído com sucesso'
                    : (progress?.message || 'Erro no backup')
            })
        }
    }, [sseEvent])

    const aoCarregar = (hosts) => {
        setTableData(hosts || [])
        // Quem já está na fila ou rodando entra no stream. O hook só conecta
        // enquanto existe alguém pra ouvir.
        const ativos = (hosts || [])
            .filter((h) => h.queue_status === 'queued' || h.queue_status === 'running')
            .map((h) => h.id)
        setActiveHostIds((prev) => [...new Set([...prev, ...ativos])])
    }

    const avisar = ({ severity, message, manterSelecao = false }) => {
        notify.setNotify({ open: true, message, severity })
        if (!manterSelecao) selecao.limpar()
        setRefresh((r) => !r)
    }

    const backupDosSelecionados = async () => {
        const ids = selecao.ids
        if (ids.length === 0) return
        setBulkBusy(true)
        let ok = 0
        for (const id of ids) {
            try {
                const res = await api.get(`/hermes/backup/run/${id}`)
                if (!res.data?.error) ok++
            } catch (e) { /* segue os outros */ }
        }
        setBulkBusy(false)
        avisar({ severity: 'success', message: `${ok} de ${ids.length} backup(s) enfileirado(s)` })
    }

    const backupDaEmpresa = async (soErros) => {
        setBulkBusy(true)
        try {
            const res = await api.post('/hermes/backup/run-bulk', { only_errors: soErros })
            const d = res.data || {}
            avisar({
                severity: d.enqueued > 0 ? 'success' : 'info',
                message: d.message || `${d.enqueued || 0} enfileirados (${d.skipped || 0} já na fila, ${d.total || 0} no total)`
            })
        } catch (e) {
            avisar({ severity: 'error', message: 'Erro ao enfileirar backups', manterSelecao: true })
        }
        setBulkBusy(false)
    }

    React.useEffect(() => {
        document.title = "IPV7 Hermes - Host"
        error.setErrors([])
    }, [])

    const runBackup = (hostId) => {
        setRunningBackup(prev => ({ ...prev, [hostId]: true }))
        api.get(`/hermes/backup/run/${hostId}`)
            .then((res) => {
                if (res.data?.error) {
                    notify.setNotify({ open: true, message: res.data.error, severity: 'error' })
                    return
                }
                notify.setNotify({ open: true, message: 'Backup enfileirado', severity: 'success' })
                // Entra no stream agora: o progresso chega por evento, sem
                // precisar do refresh cego de 2s que existia aqui.
                setBackupStatus((prev) => ({
                    ...prev, [hostId]: { status: 'queued', message: 'Na fila' }
                }))
                setActiveHostIds((prev) => [...new Set([...prev, hostId])])
            })
            .catch(() => {
                notify.setNotify({ open: true, message: 'Falha ao enfileirar backup', severity: 'error' })
            })
            .finally(() => {
                setRunningBackup(prev => ({ ...prev, [hostId]: false }))
            })
    }

    return (
        <>
            <FormItem
                open={formState.open}
                id={formState.id}
                handlerClose={() => setFormState({ open: false, id: null })}
                handlerSubmit={() => {
                    setFormState({ open: false, id: null })
                    setRefresh(r => !r)
                }}
                btnSubmit={formState.id ? 'Atualizar' : 'Salvar'}
                btnClose={'Fechar'}
            />
            <BulkHostDialog
                open={bulkDialog}
                onClose={() => setBulkDialog(false)}
                hostIds={selecao.ids}
                onDone={avisar}
            />

            <HostFilterBar value={hostFilters} onChange={setHostFilters} onTypesLoaded={setTypes}>
                <Tooltip title="Enfileirar backup de todos os hosts ativos da empresa">
                    <span>
                        <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}
                                disabled={bulkBusy || !auth.hasPerm('backup.create')}
                                startIcon={bulkBusy ? <CircularProgress size={16} /> : <RocketLaunchIcon />}
                                onClick={() => backupDaEmpresa(false)}>
                            Backup em massa
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title="Enfileirar backup só dos hosts com erro">
                    <span>
                        <Button size="small" variant="outlined" color="warning" sx={{ textTransform: 'none' }}
                                disabled={bulkBusy || !auth.hasPerm('backup.create')}
                                onClick={() => backupDaEmpresa(true)}>
                            Só com erro
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title="Adicionar host">
                    <IconButton disabled={!auth.hasPerm('host.create')}
                                onClick={() => setFormState({ open: true, id: null })}>
                        <AddIcon />
                    </IconButton>
                </Tooltip>
                <BtnZabbix callback={() => setRefresh(r => !r)} />
            </HostFilterBar>

            <ExecucoesHostDialog
                open={execDialog.open}
                hostId={execDialog.hostId}
                hostName={execDialog.hostName}
                onClose={() => setExecDialog({ open: false, hostId: null, hostName: '' })}
            />

            <SelecaoHostsDialog
                open={verSelecao}
                onClose={() => setVerSelecao(false)}
                itens={selecao.itens}
                onRemover={selecao.remover}
                onLimpar={() => { selecao.limpar(); setVerSelecao(false) }}
            >
                <Button size="small" variant="outlined" startIcon={<PlayArrow />}
                    disabled={bulkBusy || !auth.hasPerm('backup.create')}
                    onClick={() => { setVerSelecao(false); backupDosSelecionados() }}
                    sx={{ textTransform: 'none' }}>
                    Executar backup
                </Button>
                <Button size="small" variant="contained" color="warning" startIcon={<TuneIcon />}
                    disabled={bulkBusy || !auth.hasPerm('host.update')}
                    onClick={() => { setVerSelecao(false); setBulkDialog(true) }}
                    sx={{ textTransform: 'none' }}>
                    Alteração em massa
                </Button>
            </SelecaoHostsDialog>

            <SelecaoHostsBar
                total={selecao.ids.length}
                nestaPagina={selecao.rowsSelected.length}
                onLimpar={selecao.limpar}
                onVerSelecao={() => setVerSelecao(true)}
            >
                <Button size="small" variant="outlined" startIcon={<PlayArrow />}
                        disabled={bulkBusy || !auth.hasPerm('backup.create')}
                        onClick={backupDosSelecionados}
                        sx={{ textTransform: 'none' }}>
                    Executar backup
                </Button>
                <Button size="small" variant="outlined" color="warning" startIcon={<TuneIcon />}
                        disabled={bulkBusy || !auth.hasPerm('host.update')}
                        onClick={() => setBulkDialog(true)}
                        sx={{ textTransform: 'none' }}>
                    Alteração em massa
                </Button>
                {bulkBusy && <CircularProgress size={18} />}
            </SelecaoHostsBar>

            <Datatable
                refresh={refresh}
                handlerRefresh={() => setRefresh(r => !r)}
                title='Hosts'
                endpoint='/hermes/host'
                filters={toDatatableFilters(hostFilters)}
                onDataLoaded={aoCarregar}
                options={{
                    selectableRows: 'multiple',
                    rowsSelected: selecao.rowsSelected,
                    onRowSelectionChange: selecao.onRowSelectionChange,
                    // A barra de seleção vive fora da tabela (ver SelecaoHostsBar).
                    // `customToolbarSelect` vazio ainda deixaria a barra nativa
                    // desenhada com "N linha(s) selecionada(s)" — duplicando o
                    // contador. `none` remove a barra inteira.
                    selectToolbarPlacement: 'none',
                    // Host inativo: row em cinza-claro, opacity reduzida.
                    setRowProps: (row) => {
                        // status fica no índice 15 (escondido) — ver columns.
                        const isInactive = row[15] === 0 || row[15] === false;
                        if (isInactive) {
                            return { style: { backgroundColor: grey[100], opacity: 0.7 } };
                        }
                        return {};
                    },
                }}
                columns={[
                    { name: 'id', label: 'ID', options: { display: false, filter: false, sort: false } },
                    { name: 'ip', label: 'IP', options: { display: false } },
                    { name: 'vendor_name', label: 'Marca', options: { display: false } },
                    { name: 'type_name', label: 'Tipo', options: { display: false } },
                    { name: 'last_backup_id', label: 'Last backup id', options: { display: false } },
                    { name: 'last_backup_date', label: 'Último backup', options: { display: false } },
                    { name: 'last_log_status', label: 'Log status', options: { display: false } },
                    { name: 'last_log_message', label: 'Log msg', options: { display: false } },
                    { name: 'unresolved_log_count', label: 'Erros pendentes', options: { display: false } },
                    { name: 'backup_count', label: 'Qtd backups', options: { display: false } },
                    { name: 'queue_status', label: 'Queue', options: { display: false } },
                    { name: 'queue_position', label: 'Pos', options: { display: false } },
                    { name: 'queue_progress', label: 'Prog', options: { display: false } },
                    {
                        name: 'host_name', label: 'Host',
                        options: {
                            customBodyRender: (value, tableMeta) => (
                                // 0=id, 1=ip, 2=vendor, 3=type, ..., 13=host_name
                                <HostCell name={value} ip={tableMeta.rowData[1]}
                                          vendor={tableMeta.rowData[2]} type={tableMeta.rowData[3]} />
                            )
                        }
                    },
                    {
                        name: 'backup_info', label: 'Backup',
                        options: {
                            filter: false, sort: false,
                            customBodyRender: (_, tableMeta) => (
                                <BackupStatus
                                    vivo={backupStatus[tableMeta.rowData[0]]}
                                    lastBackupDate={tableMeta.rowData[5]}
                                    lastLogStatus={tableMeta.rowData[6]}
                                    lastLogMessage={tableMeta.rowData[7]}
                                    unresolvedCount={tableMeta.rowData[8]}
                                    backupCount={tableMeta.rowData[9]}
                                    queueStatus={tableMeta.rowData[10]}
                                    queuePosition={tableMeta.rowData[11]}
                                    queueProgress={tableMeta.rowData[12]} />
                            )
                        }
                    },
                    // status segue no select (vem do back), só não é exibido —
                    // usado pelo setRowProps acima pra cinzar host inativo.
                    { name: 'status', label: 'Ativo', options: { display: false } },
                    {
                        name: 'actions', label: 'Ações',
                        options: {
                            filter: false, sort: false,
                            customBodyRender: (_, tableMeta) => {
                                const hostId = tableMeta.rowData[0]
                                const lastBackupId = tableMeta.rowData[4]
                                const queueStatus = tableMeta.rowData[10]
                                const busy = runningBackup[hostId] || queueStatus === 'running' || queueStatus === 'queued'
                                return (
                                    <Box display="flex" gap={0.25}>
                                        <Tooltip title="Histórico de execuções">
                                            <IconButton size="small"
                                                        onClick={() => setExecDialog({
                                                            open: true, hostId,
                                                            hostName: tableMeta.rowData[13]
                                                        })}>
                                                <HistoryIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Editar">
                                            <span>
                                                <IconButton size="small" disabled={!auth.hasPerm('host.update')}
                                                            onClick={() => setFormState({ open: true, id: hostId })}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={busy ? 'Já em execução/fila' : 'Executar backup'}>
                                            <span>
                                                <IconButton size="small"
                                                            disabled={!auth.hasPerm('backup.create') || busy}
                                                            onClick={() => runBackup(hostId)}>
                                                    {runningBackup[hostId]
                                                        ? <CircularProgress size={16} />
                                                        : <PlayArrow fontSize="small" />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {lastBackupId && (
                                            <Tooltip title="Visualizar último backup">
                                                <span>
                                                    <BackupViewButton id={lastBackupId} />
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )
                            }
                        }
                    }
                ]}
            />
        </>
    )
}




function FormItem({ id, open, handlerClose, handlerSubmit, btnClose = 'Fechar', btnSubmit = 'Salvar' }) {
    const notify = useNotify()
    const error = useFormError()
    const auth = useAuth()
    const [isLoaded, setIsLoaded] = React.useState(true)
    const modelState = {
        host_name: '',
        type_vendor: null,
        ip_address: '',
        username: '',
        password: '',
        enable_password: '',
        port: '',
        status: 1,


    }
    const [state, setState] = useState(modelState)
    const [showPassword, setShowPassword] = useState(false)
    useEffect(() => {
        if (!open) return
        setState(modelState)
        error.setErrors([])
        if (id) {
            setIsLoaded(false)
            get_one('/hermes/host', id).then(res => {
                setState({
                    host_name: res.data.data.host_name,
                    type: res.data.data.type,
                    ip_address: res.data.data.ip_address,
                    username: res.data.data.username,
                    password: '********',
                    enable_password: '********',
                    port: res.data.data.port,
                    status: res.data.data.status,
                    interface: res.data.data.interface
                })
                setIsLoaded(true)
            }).catch(e => {
                console.log(e)
                setIsLoaded(true)
            })
        }
    }, [id, open])



    const handleChange = (event) => {
        setState({ ...state, [event.target.name]: event.target.value });
    }

    const wrapperSubmit = () => {

        if (id) {
            update('/hermes/host', id, state).catch(e => notify
                .setNotify({ open: true, message: e.message, severity: 'error' }))
                .then(res => {
                    console.log(res)
                    if (res.data.error) {
                        notify.setNotify({ open: true, message: res.data.error, severity: 'error' })
                        return
                    }
                    if (res.data.errors) {
                        console.log(res.data.errors)
                        error.setErrors(res.data.errors)
                        return
                    }
                    handlerSubmit()
                    notify.setNotify({ open: true, message: 'Host atualizado com sucesso', severity: 'success' })
                    error.setErrors([])
                }).catch(e => notify.setNotify({ open: true, message: e.message, severity: 'error' }))
        }
        else {
            create('/hermes/host/create', state)
                .then(res => {
                    if (res.data.error) {
                        notify.setNotify({ open: true, message: res.data.error, severity: 'error' })
                        return
                    }
                    if (res.data.errors) {
                        error.setErrors(res.data.errors)
                        return
                    }
                    handlerSubmit()
                    notify.setNotify({ open: true, message: 'Host criado com sucesso.', severity: 'success' })
                    error.setErrors([])
                })
                .catch(e =>
                    notify.setNotify({ open: true, message: e.message, severity: 'error' }))
        }

    }
    return (
        <Box component={"form"}
            onSubmit={wrapperSubmit}
            sx={{ m: 2 }}
            display={'flex'}
            flexWrap={'wrap'}
        >

            <FormDialog
                isLoaded={isLoaded}
                title={
                    <Box display={"flex"} justifyContent={'space-between'}>
                        <Typography variant="h6" component="div">{id ? 'Editar' : 'Adicionar'} Host</Typography>
                        <IconButton disabled={!auth.hasPerm("host.delete")}
                            sx={{ display: id ? 'block' : 'none' }}
                            onClick={() => {
                                if (confirm('Deseja excluir o Host?')) {
                                    delete_one('/hermes/host', id).then(res => {
                                        notify.setNotify({ open: true, message: 'Host deletado com sucesso.', severity: 'success' })
                                        handlerClose()
                                        handlerSubmit()
                                    })
                                }
                            }}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                }
                open={open}
                handlerSubmit={wrapperSubmit}
                handlerClose={handlerClose}
                btnSubmit={btnSubmit}
                btnClose={btnClose}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }} pt={1} gap={1}>
                    <CustomTextField

                        error={error.verify('host_name')}
                        helperText={error.msgError('host_name')}
                        label='Nome do Host'
                        value={state?.host_name}
                        name={'host_name'}
                        onChange={handleChange}
                    />
                    <HermesTypeSelect
                        fullWidth
                        handler={(type) => setState(prev => ({ ...prev, type }))}
                        value={state?.type}
                        size="small"
                    />
                    <CustomTextField

                        error={error.verify('ip_address')}
                        helperText={error.msgError('ip_address')}
                        label='IP'
                        value={state?.ip_address}
                        name={'ip_address'}
                        onChange={handleChange}
                    />
                    <CustomTextField

                        error={error.verify('username')}
                        helperText={error.msgError('username')}
                        label='Usuário'
                        value={state?.username}
                        name={'username'}
                        onChange={handleChange}
                    />
                    <CustomTextField
                        id="password"
                        error={error.verify('password')}
                        helperText={error.msgError('password')}
                        label='Senha'
                        value={state.password}
                        name={'password'}
                        onChange={handleChange}
                        onBlur={(e) => {
                            if (e.target.value == '' && id) {
                                setState(prev => ({ ...prev, password: '********' }))
                            }
                        }}
                        onFocus={(e) => {
                            if (e.target.value == '********' && id) {
                                setState(prev => ({ ...prev, password: '' }))
                            }
                        }}
                        type={showPassword ? "text" : "password"}
                        InputProps={{
                            endAdornment: (
                            <IconButton
                                disabled={state.password == '********'}
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                            )
                          }}
                    />
                    <CustomTextField
                        id="enable_password"
                        error={error.verify('enable_password')}
                        helperText={error.msgError('enable_password')}
                        label='Enable Password'
                        value={state.enable_password}
                        name={'enable_password'}
                        onChange={handleChange}
                        onBlur={(e) => {
                            if (e.target.value == '' && id) {
                                setState(prev => ({ ...prev, enable_password: '********' }))
                            }
                        }}
                        onFocus={(e) => {
                            if (e.target.value == '********' && id) {
                                setState(prev => ({ ...prev, enable_password: '' }))
                            }
                        }}
                        type={showPassword && id ? "text" : "password"}
                        InputProps={{
                            endAdornment: (
                            <IconButton
                                disabled={state.enable_password == '********'}
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                            )
                          }}
                    />
                    <CustomTextField
                        error={error.verify('port')}
                        helperText={error.msgError('port')}
                        label='Porta'
                        value={state?.port}
                        name={'port'}
                        onChange={handleChange}
                    />
                    <TunnelSelect
                        fullWidth
                        admin={false}
                        handler={(tunnel) => setState(prev => ({ ...prev, interface: tunnel }))}
                        value={state?.interface}
                        size="small"
                    />
                    <FormGroup>
                        <FormControlLabel control={
                            <Checkbox
                                checked={state?.status}
                                onChange={(e) => setState({ ...state, status: e.target.checked ? 1 : 0 })}
                            />
                        }
                            label="Ativo"
                        />
                    </FormGroup>



                </Box>

            </FormDialog>
        </Box>
    )
}
